import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { format, addDays, startOfToday, isSameDay } from "date-fns";
import { AlertCircle, Calendar as CalendarIcon, Check, X } from "lucide-react";

export default function AmenitiesAvailability() {
  const [selectedAmenityId, setSelectedAmenityId] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(startOfToday());

  // Fetch amenities
  const { data: amenities = [], isLoading: amenitiesLoading } = trpc.amenities.list.useQuery();

  // Fetch bookings for selected amenity
  const { data: bookings = [], isLoading: bookingsLoading } = trpc.amenities.getBookings.useQuery(
    { amenityId: selectedAmenityId || 0 },
    { enabled: selectedAmenityId !== null }
  );

  // Initialize with first amenity
  useEffect(() => {
    if (amenities.length > 0 && selectedAmenityId === null) {
      setSelectedAmenityId(amenities[0].id);
    }
  }, [amenities, selectedAmenityId]);

  const currentAmenity = amenities.find((a: any) => a.id === selectedAmenityId);

  // Get booked dates for the selected amenity
  const getBookedDates = () => {
    if (!bookings) return new Set();
    const bookedDates = new Set<string>();
    bookings.forEach((booking: any) => {
      if (booking.status === "confirmed" || booking.status === "pending") {
        bookedDates.add(booking.bookingDate);
      }
    });
    return bookedDates;
  };

  const bookedDates = getBookedDates();

  // Get availability status for a date
  const getDateStatus = (date: Date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return bookedDates.has(dateStr) ? "booked" : "available";
  };

  // Generate calendar data for next 90 days
  const generateCalendarDays = () => {
    const days = [];
    for (let i = 0; i < 90; i++) {
      days.push(addDays(startOfToday(), i));
    }
    return days;
  };

  const calendarDays = generateCalendarDays();

  // Group days by month
  const daysByMonth = calendarDays.reduce((acc: any, day: Date) => {
    const monthKey = format(day, "yyyy-MM");
    if (!acc[monthKey]) {
      acc[monthKey] = [];
    }
    acc[monthKey].push(day);
    return acc;
  }, {});

  const monthKeys = Object.keys(daysByMonth).sort();

  if (amenitiesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading amenities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="text-4xl font-bold mb-4">Amenities Availability</h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Check availability of community amenities for the next 90 days
          </p>
        </div>

        {/* Amenity Selection */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Select Amenity</CardTitle>
            <CardDescription>Choose an amenity to view its availability</CardDescription>
          </CardHeader>
          <CardContent>
            {amenities.length === 0 ? (
              <p className="text-gray-600">No amenities available</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {amenities.map((amenity: any) => (
                  <Button
                    key={amenity.id}
                    variant={selectedAmenityId === amenity.id ? "default" : "outline"}
                    onClick={() => setSelectedAmenityId(amenity.id)}
                    className="justify-start h-auto py-3"
                  >
                    <div className="text-left">
                      <p className="font-semibold">{amenity.name}</p>
                      <p className="text-xs text-gray-500">{amenity.category}</p>
                    </div>
                  </Button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Main Content */}
        {selectedAmenityId && currentAmenity && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Calendar Preview */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5" />
                    Quick View
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => date < startOfToday()}
                    className="rounded-md border"
                  />

                  {selectedDate && (
                    <div className="mt-6 p-4 bg-muted rounded-lg">
                      <p className="text-sm font-medium mb-2">
                        {format(selectedDate, "MMMM d, yyyy")}
                      </p>
                      <div className="flex items-center gap-2">
                        {getDateStatus(selectedDate) === "available" ? (
                          <>
                            <Check className="w-4 h-4 text-green-600" />
                            <span className="text-sm text-green-600">Available</span>
                          </>
                        ) : (
                          <>
                            <X className="w-4 h-4 text-red-600" />
                            <span className="text-sm text-red-600">Booked</span>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Detailed Calendar */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>{currentAmenity.name} - Availability Calendar</CardTitle>
                  <CardDescription>
                    Green = Available | Red = Booked | Next 90 days
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {bookingsLoading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                        <p className="text-gray-600">Loading availability...</p>
                      </div>
                    ) : (
                      monthKeys.map((monthKey) => (
                        <div key={monthKey}>
                          <h3 className="font-semibold mb-3 text-lg">
                            {format(daysByMonth[monthKey][0], "MMMM yyyy")}
                          </h3>
                          <div className="grid grid-cols-7 gap-2">
                            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                              <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                                {day}
                              </div>
                            ))}
                            {daysByMonth[monthKey].map((day: Date) => {
                              const status = getDateStatus(day);
                              const isToday = isSameDay(day, startOfToday());
                              const isSelected = selectedDate && isSameDay(day, selectedDate);

                              return (
                                <button
                                  key={format(day, "yyyy-MM-dd")}
                                  onClick={() => setSelectedDate(day)}
                                  className={`
                                    aspect-square rounded-md text-sm font-medium transition-colors
                                    ${isToday ? "ring-2 ring-primary" : ""}
                                    ${isSelected ? "ring-2 ring-blue-500" : ""}
                                    ${
                                      status === "available"
                                        ? "bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-100"
                                        : "bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900 dark:text-red-100 cursor-not-allowed"
                                    }
                                  `}
                                  disabled={status === "booked"}
                                >
                                  {format(day, "d")}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Legend */}
                  <div className="mt-8 p-4 bg-muted rounded-lg">
                    <h4 className="font-semibold mb-3">Legend</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-green-100 dark:bg-green-900 rounded"></div>
                        <span className="text-sm">Available for booking</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-red-100 dark:bg-red-900 rounded"></div>
                        <span className="text-sm">Already booked</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 border-2 border-primary rounded"></div>
                        <span className="text-sm">Today</span>
                      </div>
                    </div>
                  </div>

                  {/* Booking Info */}
                  <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                    <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">About This Amenity</h4>
                    <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                      {currentAmenity.description || "No description available"}
                    </p>
                    {currentAmenity.pricingTiers && currentAmenity.pricingTiers.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">Pricing:</p>
                        <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                          {currentAmenity.pricingTiers.map((tier: any) => (
                            <li key={tier.id}>
                              • {tier.name}: ₹{tier.price}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Empty State */}
        {amenities.length === 0 && (
          <Card>
            <CardContent className="pt-6 text-center">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p className="text-gray-600">No amenities available at this time</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
